import { useMemo, type ReactElement } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { type ColumnFiltersState } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import DetailedMetricRow from "../../../core/blocks/DetailedMetricRow";
import WorkflowIcon from "../../../../assets/images/icons/workflow.svg";
import ThemeLine from "../ThemeLine";
import getMetricSpec from "../../../../modules/merchandising/utils/metrics";
import { useGetV4ProductReportTotals } from "../../../../modules/perf_reports/api/product/v4ProductReportTotals";
import { useGetV4ProductReportByDate } from "../../../../modules/perf_reports/api/product/v4ProductReportByDate";

interface NPICardProps {
  category?: string | null;
}

const NPICard = ({ category = null }: NPICardProps): ReactElement => {
  const { t } = useTranslation(["merchandising"]);
  const npiFilters = useMemo((): ColumnFiltersState => {
    const filters: ColumnFiltersState = [{ id: "npi_enrolled", value: { op: "=", value: true } }];
    if (category) {
      filters.push({ id: "category_id", value: { op: "=", value: category } });
    }
    return filters;
  }, [category]);

  const totalsOptionsGetter = useGetV4ProductReportTotals();
  const { data: totalsData, isLoading: totalsLoading } = useQuery({
    ...totalsOptionsGetter({ filters: npiFilters }),
    placeholderData: keepPreviousData,
  });

  const byDateOptionsGetter = useGetV4ProductReportByDate();
  const { data: byDateData, isLoading: byDateLoading } = useQuery({
    ...byDateOptionsGetter({ filters: npiFilters }),
    placeholderData: keepPreviousData,
  });

  const isLoading = totalsLoading || byDateLoading;

  const metrics = useMemo(() => {
    return [
      {
        ...getMetricSpec("sales"),
        name: ($) => $.metrics["Enrolled Sales"],
        toolTip: ($) => $.metrics["Total Sales from products enrolled in New Product Introduction"],
      },
      getMetricSpec("units"),
      {
        ...getMetricSpec("rating"),
        name: ($) => $.metrics["Rating"],
        toolTip: ($) => $.metrics["The average rating across all products enrolled in New Product Introduction"],
        totalOverrideAccessor: "last_rating",
      },
      {
        ...getMetricSpec("ratings_total"),
        name: ($) => $.metrics["# of Ratings"],
        toolTip: ($) => $.metrics["The number of ratings for all products enrolled in New Product Introduction"],
        totalOverrideAccessor: "last_ratings_total",
      },
    ];
  }, []);

  return (
    <DetailedMetricRow
      title={t(($) => $.merchandising["New Product Launch"])}
      to={`/user/productlaunch`}
      byline={
        <>
          <ThemeLine>{t(($) => $.merchandising["Workflows"])}</ThemeLine>
          {t(($) => $.merchandising["Create new product launches and track their performance"])}
          {isLoading && <i className="fa fa-spinner fa-spin"></i>}
        </>
      }
      totals={{ data: totalsData?.data, loading: isLoading }}
      timeline={{ data: byDateData?.data, loading: isLoading }}
      metrics={metrics}
      icon={WorkflowIcon}
    />
  );
};

export default NPICard;
